import parseHTML from 'html-react-parser';
import { Alert, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useQuery as useQueryString } from '@hotjar/webapp/utils/routing/utils/get-route';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { useOwnedAccount } from '@hotjar/account';

import type { PBContext } from './types';
import { PBReferrers, PBBannerKinds } from './types';

const Container = styled.div`
  padding: 0;
`;

const StyledAlert = styled(Alert)`
  border-radius: 0;
  padding-left: ${tokens.spacer.x4};
`;

/**
 * This shows a contextual message on the Plans & Billing page
 *
 * How to use?
 * Redirect to the plans & billing page and add a pb-context query string
 * in the shape of [PBContext](@hotjar/webapp/features/Billing/ContextualBanner/types.ts)
 *
 * The [toPlansAndBillingWithContext](@hotjar/webapp/features/Billing/ContextualBanner/utils/toPlansAndBillingWithContext.ts)
 * util has been created to make it easy to redirect to P&B with the correct context
 *
 * Required param:
 *  * `referrer` - Where is the traffic coming from i.e. what do we need to provide context on
 *
 * Optional params:
 *  * `kind` - This toggles the colour of the banner based on the various designs. The default is info.
 *  * `i18nData` - An object of key value pairs that can be used to pass variable values to the i18n string template
 *
 */
export const ContextualBanner = (): JSX.Element | null => {
  const urlQuery = useQueryString();
  const { t } = useTranslationWithPrefix('billing:context-messaging');
  const pbContext = urlQuery.get('pb-context');
  const { data: account } = useOwnedAccount();
  const { viewElement, getGroupDataForAccount } = useTrack();
  const contextData: PBContext | null = pbContext
    ? JSON.parse(decodeURIComponent(pbContext))
    : null;

  useEffectOnceWithDependencies(
    () => {
      if (!contextData || !account) {
        return;
      }

      viewElement(
        'Plans & Billing contextual banner',
        contextData.referrer,
        getGroupDataForAccount(account),
      );
    },
    [],
    () => !!contextData && !!account,
  );

  if (!contextData) {
    return null;
  }

  if (!Object.values(PBReferrers).includes(contextData.referrer as PBReferrers)) {
    return null;
  }

  // This takes care of removing html tags from the json values
  // We are doing this in order to prevent bad actors from injecting random html
  // into this page
  const i18nData: PBContext['i18nData'] = {};
  if (contextData.i18nData && Object.keys(contextData.i18nData).length) {
    Object.keys(contextData.i18nData).forEach((key) => {
      const item = contextData?.i18nData?.[key].toString();
      if (item) {
        i18nData[key] = item.replace(/(<([^>]+)>)/gi, '');
      }
    });
  }

  return (
    <Container>
      <StyledAlert kind={contextData.kind || PBBannerKinds.INFO}>
        <span data-testid="pb-contextual-message">
          {parseHTML(t(`${contextData.referrer}`, { ...i18nData }))}
        </span>
      </StyledAlert>
    </Container>
  );
};
